/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { X } from 'lucide-react'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer'
import * as ConfirmDialogModal from './confirm-dialog-modal'
import * as CreateQuestionModal from './create-question-modal'
import * as CreatePatientModal from './create-patient-modal'
import { CreateServiceModalContent } from './create-service-modal'
import * as EditPatientModal from './edit-patient-modal'
import * as EditQuestionModal from './edit-question-modal'
import * as QuestionViewModal from './question-view-modal'
import * as ShareInviteCodeModal from './share-invite-code-modal'

import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import {
  Modal,
  ModalPlacement,
  ModalRenderArgs,
  ModalRenderContext,
  ModalSlots,
  ModalVariant,
  useModalControls,
  useModals,
} from '@/state/modals'

export type ModalRendererFn<M extends Modal = Modal> = (args: ModalRenderArgs<M>) => React.ReactNode
export type LazyModalRenderer<M extends Modal = Modal> = {
  lazy: () => Promise<ModalRendererFn<M> | { default: ModalRendererFn<M> }>
}
export type ModalRegistry = {
  [K in Modal['name']]: ModalRendererFn<Extract<Modal, { name: K }>> | LazyModalRenderer<Extract<Modal, { name: K }>>
}

const lazyRegistryCache = new Map<Modal['name'], ModalRendererFn>()
const ModalLayoutContext = React.createContext<{ slots: ModalSlots; ctx: ModalRenderContext } | null>(null)

const modalRegistry: ModalRegistry = {
  'create-service': (args) => <CreateServiceModalContent {...args} />,
  'edit-patient': ({ modal }) => <EditPatientModal.Component {...modal} />,
  'question-view': ({ modal }) => <QuestionViewModal.Component {...modal} />,
  'create-question': CreateQuestionModal.render,
  'edit-question': ({ modal }) => <EditQuestionModal.Component {...modal} />,
  'share-invite-code': ({ modal }) => <ShareInviteCodeModal.Component {...modal} />,
  'create-patient': CreatePatientModal.render,
  confirm: ConfirmDialogModal.render,
}

const isLazyRenderer = (entry: ModalRegistry[keyof ModalRegistry]): entry is LazyModalRenderer =>
  typeof (entry as LazyModalRenderer).lazy === 'function'

export function ModalsContainer() {
  const { isModalActive, activeModals } = useModals()
  const { closeModal, closeAllModals, resolveModal } = useModalControls()

  const [resolvedRenderer, setResolvedRenderer] = React.useState<ModalRendererFn | null>(null)
  const [isRendererLoading, setIsRendererLoading] = React.useState(false)

  const activeModal = activeModals[activeModals.length - 1]

  const onChange = async (isActive: boolean) => {
    if (!isActive) {
      closeModal()
    }
  }

  const variant: ModalVariant = activeModal?.variant ?? 'auto'
  const placement: ModalPlacement = activeModal?.placement ?? 'bottom'
  const dismissible = activeModal?.dismissible ?? true

  const ctx = React.useMemo(
    () => ({ close: closeModal, closeAll: closeAllModals, resolve: resolveModal }),
    [closeModal, closeAllModals, resolveModal],
  )

  React.useEffect(() => {
    let cancelled = false

    const loadRenderer = async () => {
      if (!activeModal) {
        setResolvedRenderer(null)
        setIsRendererLoading(false)
        return
      }

      const entry = modalRegistry[activeModal.name]
      if (!entry) {
        setResolvedRenderer(null)
        setIsRendererLoading(false)
        return
      }

      if (!isLazyRenderer(entry)) {
        setResolvedRenderer(() => entry as ModalRendererFn)
        setIsRendererLoading(false)
        return
      }

      const cached = lazyRegistryCache.get(activeModal.name)
      if (cached) {
        setResolvedRenderer(() => cached)
        setIsRendererLoading(false)
        return
      }

      setIsRendererLoading(true)
      try {
        const loaded = await entry.lazy()
        if (cancelled) return
        const renderer = typeof loaded === 'function' ? loaded : loaded.default
        if (renderer) {
          lazyRegistryCache.set(activeModal.name, renderer as ModalRendererFn)
          setResolvedRenderer(() => renderer as ModalRendererFn)
        }
      } finally {
        if (!cancelled) {
          setIsRendererLoading(false)
        }
      }
    }

    loadRenderer()

    return () => {
      cancelled = true
    }
  }, [activeModal])

  if (!activeModal) {
    return null
  }

  const renderModal = (
    activeModal.render ? ({ slots, ctx }: ModalRenderArgs) => activeModal.render!(slots, ctx) : resolvedRenderer
  ) as ModalRendererFn | null

  const isLoading = isRendererLoading || (!renderModal && Boolean(activeModal))

  if (!renderModal && !isLoading) return null

  return (
    <ResponsiveModal
      ctx={ctx}
      isActive={isModalActive}
      onChange={onChange}
      variant={variant}
      placement={placement}
      dismissible={dismissible}
    >
      {(slots) =>
        renderModal ? (
          renderModal({ modal: activeModal, slots, ctx })
        ) : (
          <div className="space-y-2 p-6 text-sm text-muted-foreground">
            <slots.Header className="sr-only">
              <slots.Title>Chargement…</slots.Title>
              <slots.Description />
            </slots.Header>
            <div>Chargement…</div>
          </div>
        )
      }
    </ResponsiveModal>
  )
}

interface ResponsiveModalProps {
  isActive: boolean
  onChange: (active: boolean) => void
  variant?: ModalVariant
  placement?: ModalPlacement
  dismissible?: boolean
  ctx: ModalRenderContext
}

type SlotComponents = ModalSlots

type ResponsiveModalChild = React.ReactNode | ((slots: SlotComponents) => React.ReactNode)

const ResponsiveModal = ({
  children,
  isActive,
  onChange,
  variant = 'auto',
  placement = 'bottom',
  dismissible = true,
  ctx,
}: ResponsiveModalProps & { children: ResponsiveModalChild }) => {
  const isMobile = useIsMobile()

  const resolvedVariant: ModalVariant = React.useMemo(() => {
    if (variant && variant !== 'auto') return variant
    if (isMobile) return 'drawer'
    return 'dialog'
  }, [variant, isMobile])

  const slots = React.useMemo(() => buildSlots(resolvedVariant, placement), [resolvedVariant, placement])

  const content = React.useMemo(() => {
    const body = typeof children === 'function' ? children(slots) : children
    return body
  }, [children, slots])

  const wrapped = React.useMemo(
    () => <ModalLayoutContext.Provider value={{ slots, ctx }}>{content}</ModalLayoutContext.Provider>,
    [content, ctx, slots],
  )

  if (resolvedVariant === 'drawer' || resolvedVariant === 'side-drawer') {
    const direction = resolvedVariant === 'side-drawer' ? 'right' : placement

    return (
      <Drawer
        open={isActive}
        onOpenChange={onChange}
        dismissible={dismissible}
        direction={direction as 'bottom' | 'top' | 'left' | 'right'}
      >
        <slots.Content>{wrapped}</slots.Content>
      </Drawer>
    )
  }

  return (
    <Dialog open={isActive} onOpenChange={onChange}>
      <slots.Content>{wrapped}</slots.Content>
    </Dialog>
  )
}

function buildSlots(variant: ModalVariant, placement: ModalPlacement): ModalSlots {
  const isDrawer = variant === 'drawer' || variant === 'side-drawer'

  const Content = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => {
    if (isDrawer) {
      return (
        <DrawerContent handle className={cn('p-4 sm:p-6', className)} {...rest}>
          {children}
        </DrawerContent>
      )
    }

    const dialogPlacement = placement === 'top' ? 'sm:top-[30%]' : undefined

    return (
      <DialogContent hideCloseButton className={cn('p-6', dialogPlacement, className)} {...rest}>
        {children}
      </DialogContent>
    )
  }

  const Header = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => {
    const Comp = isDrawer ? DrawerHeader : DialogHeader
    return (
      <Comp className={cn('pb-2', className)} {...rest}>
        {children}
      </Comp>
    )
  }

  const Body = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-col gap-4', className)} {...rest}>
      {children}
    </div>
  )

  const Footer = ({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) => {
    const Comp = isDrawer ? DrawerFooter : DialogFooter
    return (
      <Comp className={cn('pt-4', className)} {...rest}>
        {children}
      </Comp>
    )
  }

  const Title = ({ className, children, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) => {
    const Comp = isDrawer ? DrawerTitle : DialogTitle
    return (
      <Comp className={cn('text-xl', className)} {...rest}>
        {children}
      </Comp>
    )
  }

  const Description = ({ className, children, ...rest }: React.HTMLAttributes<HTMLParagraphElement>) => {
    const Comp = isDrawer ? DrawerDescription : DialogDescription
    return (
      <Comp className={className} {...rest}>
        {children}
      </Comp>
    )
  }

  const Close = ({ className, ...rest }: React.ComponentPropsWithoutRef<'button'>) => {
    const Comp = isDrawer ? DrawerClose : DialogClose
    return (
      <Comp asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-muted-foreground shadow-sm transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className,
          )}
          {...rest}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Fermer</span>
        </button>
      </Comp>
    )
  }

  return { Content, Header, Body, Footer, Title, Description, Close }
}

export const useModalLayout = () => {
  const value = React.useContext(ModalLayoutContext)
  if (!value) {
    throw new Error('Modal layout context unavailable outside a modal.')
  }
  return value
}

export const ModalHeader = ({
  title,
  description,
  showClose = true,
  className,
  actions,
}: {
  title?: React.ReactNode
  description?: React.ReactNode
  showClose?: boolean
  className?: string
  actions?: React.ReactNode
}) => {
  const { slots, ctx } = useModalLayout()
  const { Header, Title, Description, Close } = slots

  return (
    <Header className={cn('flex items-start justify-between gap-3', className)}>
      <div className="space-y-1 text-left">
        {title ? <Title className="text-lg flex items-center gap-2">{title}</Title> : null}
        {description ? <Description>{description}</Description> : null}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        {showClose ? <Close onClick={() => ctx.resolve()} /> : null}
      </div>
    </Header>
  )
}
